import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import GoogleSignIn from "../components/auth/GoogleSignIn";

/* Sign-in is the one screen with no app shell. Copy lives here rather than in
   a global i18n layer because this page ships before that layer exists; the
   chosen language is persisted so Settings can adopt it later. */
const COPY = {
  en: {
    heading: "Welcome back",
    tagline: "A little help. A better conversation.",
    email: "Email address",
    password: "Password",
    forgot: "Forgot password?",
    signIn: "Sign in",
    signingIn: "Signing in…",
    newHere: "New to Bicarra?",
    createAccount: "Create an account",
    showPassword: "Show password",
    hidePassword: "Hide password",
    resetHelp:
      "Self-serve password reset isn’t available yet. Contact us and we’ll reset it for you.",
    contactUs: "Contact us",
  },
  ms: {
    heading: "Selamat kembali",
    tagline: "Sedikit bantuan. Perbualan yang lebih baik.",
    email: "Alamat e-mel",
    password: "Kata laluan",
    forgot: "Lupa kata laluan?",
    signIn: "Log masuk",
    signingIn: "Sedang log masuk…",
    newHere: "Baru di Bicarra?",
    createAccount: "Buka akaun",
    showPassword: "Tunjuk kata laluan",
    hidePassword: "Sembunyi kata laluan",
    resetHelp:
      "Set semula kata laluan sendiri belum tersedia. Hubungi kami dan kami akan set semula untuk anda.",
    contactUs: "Hubungi kami",
  },
};

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem("bicarra.lang") === "ms" ? "ms" : "en"; }
    catch { return "en"; }
  });

  const { login, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = COPY[lang];
  const from = location.state?.from?.pathname || "/home";

  const pickLang = (next) => {
    setLang(next);
    try { localStorage.setItem("bicarra.lang", next); } catch {}
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      const message = formatApiError(err.response?.data?.detail) || err.message;
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    "w-full h-11 px-3.5 rounded-[10px] bg-white border border-b2-linestrong " +
    "text-[15px] text-b2-ink placeholder:text-b2-faint " +
    "focus:outline-none focus:border-b2-indigo focus:ring-2 focus:ring-b2-indigo/15 transition";

  return (
    <div className="b2 min-h-screen flex flex-col bg-b2-canvas">
      {/* Quiet top rail: wordmark left, language right. Nothing else. */}
      <header className="flex items-center justify-between px-6 sm:px-10 h-[72px] shrink-0">
        <Link to="/" className="flex items-center gap-2">
          <img src="/brand/bicarra-icon.svg" alt="" className="w-[26px] h-[26px] rounded-[7px]" />
          <span className="text-[19px] font-bold tracking-[-0.03em] text-b2-ink lowercase">bicarra</span>
        </Link>
        <div className="flex items-center gap-1.5 text-[13px]">
          <button
            type="button" onClick={() => pickLang("en")} data-testid="lang-en"
            className={lang === "en" ? "text-b2-indigo font-semibold" : "text-b2-faint hover:text-b2-muted"}
          >English</button>
          <span className="text-b2-faint" aria-hidden="true">/</span>
          <button
            type="button" onClick={() => pickLang("ms")} data-testid="lang-ms"
            className={lang === "ms" ? "text-b2-indigo font-semibold" : "text-b2-faint hover:text-b2-muted"}
          >BM</button>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-6 pt-[8vh] pb-16">
        <div className="w-full max-w-[400px]">
          <div className="flex flex-col items-center text-center mb-9">
            <img src="/brand/bicarra-icon.svg" alt="" className="w-12 h-12 rounded-[13px] mb-5" />
            <h1 className="text-[34px] leading-[1.15] font-bold tracking-[-0.03em] text-b2-ink">
              {t.heading}
            </h1>
            <p className="mt-2 text-[15px] text-b2-muted">{t.tagline}</p>
          </div>

          <div className="mb-6">
            <GoogleSignIn
              text="signin_with"
              onSuccess={async () => {
                await checkAuth();
                navigate(from, { replace: true });
              }}
              onError={(msg) => { setError(String(msg)); toast.error(String(msg)); }}
            />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {error && (
              <div
                role="alert" data-testid="login-error"
                className="rounded-[10px] border border-red-200 bg-red-50 px-3.5 py-2.5 text-[13px] text-red-700"
              >
                {error}
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="email" className="block text-[13px] font-medium text-b2-inksoft">
                {t.email}
              </label>
              <input
                id="email" type="email" autoComplete="email" required
                data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com"
                className={fieldClass}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="block text-[13px] font-medium text-b2-inksoft">
                {t.password}
              </label>
              <div className="relative">
                <input
                  id="password" required autoComplete="current-password"
                  type={showPassword ? "text" : "password"}
                  data-testid="login-password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`${fieldClass} pr-11`}
                />
                <button
                  type="button" tabIndex={-1}
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? t.hidePassword : t.showPassword}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-b2-faint hover:text-b2-muted"
                >
                  {showPassword ? <EyeOff className="w-[18px] h-[18px]" strokeWidth={1.8} />
                                : <Eye className="w-[18px] h-[18px]" strokeWidth={1.8} />}
                </button>
              </div>
              <div className="flex justify-end pt-0.5">
                <Link
                  to="/forgot-password" data-testid="forgot-password-btn"
                  className="text-[13px] text-b2-indigo hover:underline"
                >
                  {t.forgot}
                </Link>
              </div>
            </div>

            <button
              type="submit" disabled={loading} data-testid="login-submit-btn"
              className="w-full h-11 rounded-[10px] bg-b2-indigo hover:bg-b2-indigohover
                         text-white text-[15px] font-semibold
                         disabled:opacity-60 disabled:cursor-not-allowed
                         flex items-center justify-center gap-2 transition-colors"
            >
              {loading
                ? <><Loader2 className="w-4 h-4 animate-spin" /> {t.signingIn}</>
                : t.signIn}
            </button>
          </form>

          <p className="mt-7 text-center text-[14px] text-b2-muted">
            {t.newHere}{" "}
            <Link to="/register" className="text-b2-indigo font-medium hover:underline">
              {t.createAccount}
            </Link>
          </p>
        </div>
      </main>

      <footer className="shrink-0 pb-8 text-center space-y-0.5">
        <p className="text-[11px] tracking-[0.04em] text-b2-faint">AI with Adab &amp; Akhlak</p>
        <p className="text-[11px] tracking-[0.04em] text-b2-faint">by Veltrix</p>
      </footer>
    </div>
  );
}
